import { Eye, Users, X } from 'lucide-react';
import { useDemoCohort } from '../contexts/DemoCohortContext';

// Board / Senior-Management demo cohort login (2026-06-16).
//
// Full-screen owner picker for the shared demo login. Used in two ways:
//   - Mandatory landing gate (dismissable={false}): the demo login must pick a
//     cohort owner before reaching the portal.
//   - Switch overlay (dismissable={true}): reachable any time from the
//     persistent DemoViewBanner to switch to a different cohort owner.
//
// Selecting an owner calls the cohort-gated switch endpoint; on success the
// app hard-reloads as that owner (read-only).
export default function CohortPicker({ dismissable = false }) {
  const { owners, viewingOwnerContactId, switching, error, switchOwner, closePicker } = useDemoCohort();

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="cohort-picker-title">
      <div style={styles.card}>
        {dismissable && (
          <button type="button" onClick={closePicker} style={styles.close} aria-label="Close" title="Close">
            <X size={18} />
          </button>
        )}
        <div style={styles.head}>
          <Users size={22} style={{ flexShrink: 0 }} />
          <div>
            <h2 id="cohort-picker-title" style={styles.title}>Choose an owner to view</h2>
            <p style={styles.subtitle}>
              <Eye size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
              Demo view-only access. Pick an owner to explore their portal — you can switch any time.
            </p>
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {owners.length === 0 ? (
          <div style={styles.empty}>
            No owners are available in this demo cohort yet. Please contact your administrator.
          </div>
        ) : (
          <ul style={styles.list}>
            {owners.map((o) => {
              const active = o.owner_contact_id === viewingOwnerContactId;
              return (
                <li key={o.owner_contact_id}>
                  <button
                    type="button"
                    onClick={() => switchOwner(o.owner_contact_id)}
                    disabled={switching}
                    style={{ ...styles.item, ...(active ? styles.itemActive : {}), ...(switching ? styles.itemDisabled : {}) }}
                  >
                    <span style={styles.itemName}>{o.full_name || o.primary_email || o.owner_contact_id}</span>
                    {o.primary_email && o.full_name && (
                      <span style={styles.itemEmail}>{o.primary_email}</span>
                    )}
                    {active && <span style={styles.itemBadge}>Currently viewing</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {switching && <div style={styles.switching}>Switching…</div>}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(19, 33, 57, 0.55)',
    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9998, padding: '20px'
  },
  card: {
    position: 'relative',
    backgroundColor: 'var(--white, #ffffff)',
    borderRadius: '12px', padding: '32px',
    width: '100%', maxWidth: '480px',
    maxHeight: '80vh', overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    border: '1px solid var(--gray-100, #e5e7eb)'
  },
  close: {
    position: 'absolute', top: '14px', right: '14px',
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--gray-600, #4b5563)', padding: '4px', lineHeight: 0
  },
  head: { display: 'flex', gap: '12px', alignItems: 'flex-start', color: 'var(--navy, #132139)', marginBottom: '18px' },
  title: { fontSize: '19px', fontWeight: 700, color: 'var(--navy, #132139)', margin: '0 0 6px 0', letterSpacing: '-0.01em' },
  subtitle: { fontSize: '13px', color: 'var(--gray-600, #4b5563)', margin: 0, lineHeight: 1.5 },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' },
  item: {
    width: '100%', textAlign: 'left', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', gap: '2px',
    padding: '12px 14px', borderRadius: '8px',
    border: '1px solid var(--gray-200, #e5e7eb)', backgroundColor: 'var(--white, #fff)'
  },
  itemActive: { borderColor: 'rgba(176, 125, 42, 0.6)', backgroundColor: 'var(--gold-light, #fdf6e7)' },
  itemDisabled: { cursor: 'not-allowed', opacity: 0.6 },
  itemName: { fontSize: '14px', fontWeight: 600, color: 'var(--navy, #132139)' },
  itemEmail: { fontSize: '12px', color: 'var(--gray-600, #4b5563)' },
  itemBadge: { fontSize: '11px', fontWeight: 600, color: '#7a5a1c', marginTop: '2px' },
  empty: { fontSize: '14px', color: 'var(--gray-600, #4b5563)', padding: '12px 0', lineHeight: 1.5 },
  error: {
    backgroundColor: 'var(--red-light, #fef2f2)', border: '1px solid var(--red, #dc2626)',
    borderRadius: '8px', padding: '10px 13px', fontSize: '13px', color: 'var(--red, #dc2626)', marginBottom: '14px'
  },
  switching: { marginTop: '14px', fontSize: '13px', color: 'var(--gray-600, #4b5563)', textAlign: 'center' }
};
